import {
  DESTINATION_PAGE_COMPLETENESS_RULES,
  type WebsiteBreakdownResponse,
  type WebsiteDestinationBreakdownRow,
  type WebsiteDestinationPageCompletenessRow,
  type WebsiteHealthResponse,
  type WebsiteSurfaceKey,
} from '@utazzo/shared';

/**
 * Pure derivations for the admin Website Dashboard. Everything here reads
 * straight off the two aggregates (`/dashboard/website-health` and
 * `/dashboard/website-breakdown`); no number is hard-coded. Each formula
 * guards divide-by-zero so an empty site renders zeros rather than NaN.
 *
 * The completeness rules are the same ones the website-manager dashboard
 * grades against, so both pages agree on what "complete" means.
 */

export const RULES = DESTINATION_PAGE_COMPLETENESS_RULES;

/** Indian-grouped integer formatter, shared by every number on the page. */
export const fmt = (n: number): string => n.toLocaleString('en-IN');

/** Percentage of `part` within `whole`, rounded, 0 when `whole` is 0. */
export const pct = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

/** Palette lifted from the design: soft status chips + surface glyph tones. */
export const TONE = {
  good: { bg: '#d1fae5', fg: '#047857' },
  muted: { bg: '#f3f4f4', fg: '#55595e' },
  warn: { bg: '#fef3c7', fg: '#b45309' },
  bad: { bg: '#fee2e2', fg: '#b91c1c' },
} as const;

/** Teal accent used for the hero KPI and every emphasis on the page. */
export const TEAL = '#0c3b4d';

/** Fixed display metadata per surface - label, glyph, chip tone, list route. */
export interface SurfaceMeta {
  key: WebsiteSurfaceKey;
  label: string;
  /** Short word on the surface tile swatch. */
  glyph: string;
  bg: string;
  fg: string;
  /** List screen the tile links to. */
  to: string;
  /** Word used for the "live" half of the split ("live" reads wrong for packages). */
  liveWord: string;
}

/** Order matches the design's tile grid (blogs first, packages last). */
export const SURFACE_META: readonly SurfaceMeta[] = [
  {
    key: 'blog',
    label: 'Blogs',
    glyph: 'B',
    bg: '#ffe4e6',
    fg: '#be123c',
    to: '/blogs',
    liveWord: 'Live',
  },
  {
    key: 'blog_category',
    label: 'Blog categories',
    glyph: 'T',
    bg: '#fef3c7',
    fg: '#b45309',
    to: '/categories',
    liveWord: 'Published',
  },
  {
    key: 'page',
    label: 'Pages',
    glyph: 'P',
    bg: '#e0f2fe',
    fg: '#0369a1',
    to: '/pages',
    liveWord: 'Live',
  },
  {
    key: 'destination_page',
    label: 'Destination pages',
    glyph: 'D',
    bg: '#d1fae5',
    fg: '#047857',
    to: '/destination-pages',
    liveWord: 'Live',
  },
  {
    key: 'category_page',
    label: 'Category pages',
    glyph: 'C',
    bg: '#ede9fe',
    fg: '#6d28d9',
    to: '/category-pages',
    liveWord: 'Live',
  },
  {
    key: 'package',
    label: 'Packages',
    glyph: 'K',
    bg: '#e0e7ff',
    fg: '#4338ca',
    to: '/packages',
    liveWord: 'Published',
  },
];

/** Human plural per surface, used in the "missing SEO" chips. */
export const SURFACE_PLURAL: Record<WebsiteSurfaceKey, string> = {
  blog: 'blogs',
  blog_category: 'blog categories',
  page: 'pages',
  destination_page: 'destination pages',
  category_page: 'category pages',
  package: 'packages',
};

/** `PENDING_REVIEW` -> `In review`; `ACTIVE` reads as the site's "Published". */
export const statusLabel = (status: string): string => {
  if (status === 'ACTIVE') return 'Published';
  if (status === 'PENDING_REVIEW') return 'Pending review';
  const words = status.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const statusTone = (status: string): { bg: string; fg: string } => {
  if (status === 'PUBLISHED' || status === 'ACTIVE') return TONE.good;
  if (status === 'DRAFT') return TONE.muted;
  return TONE.warn;
};

// --- Destination-page completeness (same rules as the website-manager page) ---

export const shortSections = (
  row: WebsiteDestinationPageCompletenessRow,
): WebsiteDestinationPageCompletenessRow['sections'] =>
  row.sections.filter((s) => s.cards < RULES.minCardsPerSection);

// Reels are shown in the table for reference but are NOT a completeness
// criterion (product decision 2026-10-10) - a page is complete on banner +
// collections + cards alone.
export const isPageComplete = (row: WebsiteDestinationPageCompletenessRow): boolean =>
  row.hasBanner && row.sections.length >= RULES.minSections && shortSections(row).length === 0;

const isPublished = (status: string): boolean => status === 'PUBLISHED' || status === 'ACTIVE';

/** A destination that sells (published packages) but has no live destination page. */
export const sellsWithoutLivePage = (row: WebsiteDestinationBreakdownRow): boolean =>
  row.destinationId !== null && row.packages.live > 0 && row.destinationPages.live === 0;

/** Pending-review rows summed across every surface that reports a status tally. */
const pendingReviewTotal = (b: WebsiteBreakdownResponse): number => {
  const tallies = [
    b.packages.byStatus,
    b.blogs.byStatus,
    b.pages.byStatus,
    b.destinationPages.byStatus,
    b.categoryPages.byStatus,
  ];
  return tallies.reduce(
    (sum, rows) =>
      sum + rows.filter((r) => r.status === 'PENDING_REVIEW').reduce((s, r) => s + r.count, 0),
    0,
  );
};

export interface RuleFail {
  key: 'banner' | 'sections' | 'cards';
  /** Short chip label for the completeness KPI. */
  label: string;
  /** Verbose pill for the completeness table filters. */
  pill: string;
  n: number;
}

export interface HealthModel {
  /** Site-health headline (KPI 1). */
  liveTotal: number;
  grandTotal: number;
  draftTotal: number;
  reviewTotal: number;
  otherTotal: number;
  livePct: number;
  /** Live pages missing SEO / content (KPI 2). */
  incompleteLive: number;
  incompleteDraft: number;
  incompleteLivePct: number;
  incompleteBySurface: { surface: WebsiteSurfaceKey; label: string; live: number }[];
  /** Split of KPI 2: live pages missing SEO vs missing content (overlapping), across all surfaces. */
  missingSeoLive: number;
  missingContentLive: number;
  /**
   * Same split but DESTINATION PAGES only - the one surface whose list can be
   * filtered by SEO / content. The drill-down link is destination-only, so the
   * chip shows this number (which matches the filtered list) and keeps the
   * cross-surface total as muted context.
   */
  missingSeoLiveDp: number;
  missingContentLiveDp: number;
}

/** KPI 1 + KPI 2, from the health aggregate plus the breakdown status tallies. */
export const deriveHealth = (
  health: WebsiteHealthResponse,
  breakdown: WebsiteBreakdownResponse | undefined,
): HealthModel => {
  const grandTotal = health.surfaces.reduce((s, r) => s + r.total, 0);
  const liveTotal = health.surfaces.reduce((s, r) => s + r.live, 0);
  const draftTotal = health.surfaces.reduce((s, r) => s + r.draft, 0);
  const reviewTotal = breakdown ? pendingReviewTotal(breakdown) : 0;
  const otherTotal = Math.max(0, grandTotal - liveTotal - draftTotal - reviewTotal);

  const incompleteLive = health.incompleteCounts.reduce((s, c) => s + c.live, 0);
  const incompleteDraft = health.incompleteCounts.reduce((s, c) => s + c.draft, 0);
  // Denominator: the live rows of exactly the surfaces that can be graded as
  // incomplete, so "% of live" compares like with like (not diluted by every
  // package counting as "live").
  const gradedSurfaces = new Set(health.incompleteCounts.map((c) => c.surface));
  const gradedLive = health.surfaces
    .filter((s) => gradedSurfaces.has(s.key))
    .reduce((s, r) => s + r.live, 0);

  return {
    grandTotal,
    liveTotal,
    draftTotal,
    reviewTotal,
    otherTotal,
    livePct: pct(liveTotal, grandTotal),
    incompleteLive,
    incompleteDraft,
    incompleteLivePct: pct(incompleteLive, gradedLive),
    incompleteBySurface: health.incompleteCounts
      .filter((c) => c.live > 0)
      .sort((a, b) => b.live - a.live)
      .map((c) => ({ surface: c.surface, label: SURFACE_PLURAL[c.surface], live: c.live })),
    missingSeoLive: health.incompleteCounts.reduce((s, c) => s + c.seoLive, 0),
    missingContentLive: health.incompleteCounts.reduce((s, c) => s + c.contentLive, 0),
    missingSeoLiveDp:
      health.incompleteCounts.find((c) => c.surface === 'destination_page')?.seoLive ?? 0,
    missingContentLiveDp:
      health.incompleteCounts.find((c) => c.surface === 'destination_page')?.contentLive ?? 0,
  };
};

export interface CompletenessModel {
  dpTotal: number;
  completeCount: number;
  completePct: number;
  incompleteCount: number;
  liveIncompleteCount: number;
  ruleFails: RuleFail[];
}

/** KPI 3, graded straight off `destinationPageCompleteness`. */
export const deriveCompleteness = (
  rows: WebsiteDestinationPageCompletenessRow[],
): CompletenessModel => {
  const dpTotal = rows.length;
  const completeCount = rows.filter(isPageComplete).length;
  const incomplete = rows.filter((r) => !isPageComplete(r));
  return {
    dpTotal,
    completeCount,
    completePct: pct(completeCount, dpTotal),
    incompleteCount: incomplete.length,
    liveIncompleteCount: incomplete.filter((r) => isPublished(r.status)).length,
    ruleFails: [
      {
        key: 'banner',
        label: 'no banner',
        pill: 'Banner',
        n: rows.filter((r) => !r.hasBanner).length,
      },
      {
        key: 'sections',
        label: `collections < ${RULES.minSections}`,
        pill: `Collection under ${RULES.minSections}`,
        n: rows.filter((r) => r.sections.length < RULES.minSections).length,
      },
      {
        key: 'cards',
        label: 'thin collections',
        pill: `Collection under ${RULES.minCardsPerSection} cards`,
        n: rows.filter((r) => shortSections(r).length > 0).length,
      },
    ],
  };
};

/** KPI 4 "Where to look next" - four actionable gaps, all from the breakdown. */
export interface GapsModel {
  sellingNoPage: number;
  liveIncomplete: number;
  blogless: number;
  thinCategoryPages: number;
}

export const deriveGaps = (
  breakdown: WebsiteBreakdownResponse,
  completeness: CompletenessModel,
): GapsModel => {
  const linked = breakdown.destinations.filter((d) => d.destinationId !== null);
  return {
    sellingNoPage: linked.filter(sellsWithoutLivePage).length,
    liveIncomplete: completeness.liveIncompleteCount,
    blogless: linked.filter((d) => d.blogs.total === 0).length,
    thinCategoryPages: breakdown.categoryPages.rows.filter(
      (r) =>
        isPublished(r.status) &&
        (r.destinationPages < RULES.minSections || r.bestSellers < RULES.minSections),
    ).length,
  };
};
